import type React from 'react';
import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, description, children, size = 'md' }) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-4">
        <DialogPanel
          className={cn(
            'relative bg-white w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden shadow-elevated',
            'rounded-t-2xl md:rounded-2xl',
            sizeClasses[size],
          )}
        >
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100">
            <div>
              {title && <DialogTitle className="text-lg font-semibold text-gray-900">{title}</DialogTitle>}
              {description && <Description className="text-sm text-gray-500 mt-0.5">{description}</Description>}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              aria-label="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 md:px-6 py-5 max-h-[70vh] md:max-h-[60vh] overflow-y-auto">
            {children}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default Modal;
